import type { AppState, ClassificationScenario, ScenarioMark, ScenarioState } from '../core/types';
import { activeArea, colorForCategory } from '../domain/classification';
import { partialFragmentCategories } from '../domain/scenarioPartials';
import { assignScenario, clearScenarioTargets, createScenario, replaceScenario, scenarioStatus, scenarioTargets, shownScenario, type ScenarioAssignments, type ScenarioTarget } from '../domain/scenarios';
import type { CategoryStyle, ClassificationConfig } from './config';
import { commitClassification } from './legendDesign';
import { masterThemeAssignments, type MasterTheme } from './masterTheme';

/**
 * Panel actions of V1.9.0 scenarios. Each returns a store patch: `scenarios` and, when a proposed category is new,
 * the classification (its categories gain a `proposed` style). The Excel, the project fields and the Maestro are never
 * part of a patch.
 */

/** The one identification of a proposal wherever it is presented (legend or badge, screen and PNG). */
export const scenarioMarkText = (name: string): string => `ESCENARIO PROPUESTO · ${name} · No ejecutado`;

/** Placement of that mark: in the legend when chosen and the legend is drawn, otherwise a badge (never both). */
export const scenarioMarkPlacement = (mark: ScenarioMark | undefined, legendDrawn: boolean): ScenarioMark =>
  mark === 'top' || mark === 'bottom' ? mark : legendDrawn ? 'legend' : 'top';

export const setScenarioMark = (state: AppState, mark: ScenarioMark): Pick<AppState, 'scenarios'> => {
  const { mark: _previous, ...rest } = state.scenarios; void _previous;
  return { scenarios: mark === 'legend' ? rest : { ...rest, mark } };
};

const masterLayer = (state: AppState) => state.layers.find(({ id, kind }) => kind === 'master' && id === state.classificationConfig.layerId);

/** The scenario drawn now (shown AND applicable to the active classification of the Maestro), or null. */
export function drawnScenario(state: AppState): ClassificationScenario | null {
  const scenario = shownScenario(state.scenarios);
  return scenario && masterLayer(state) && scenarioStatus(scenario, state.classificationConfig).applies ? scenario : null;
}

/**
 * Why the shown scenario cannot be edited now, or null when it can. Assignments are only accepted while the scenario
 * is drawn over the classification it was created for: otherwise a proposed category would be added to another field.
 */
export function scenarioEditBlock(state: AppState): string | null {
  const scenario = shownScenario(state.scenarios);
  if (!scenario) return 'Elige o crea una propuesta.';
  if (!state.model || !masterLayer(state)) return 'Clasifica el Maestro para editar la propuesta.';
  return scenarioStatus(scenario, state.classificationConfig).reason ?? null;
}

export function newScenario(state: AppState, name: string, id = `escenario-${Date.now()}`, now = new Date().toISOString()): Pick<AppState, 'scenarios'> | null {
  if (!state.classificationConfig.field || state.classificationConfig.mode !== 'categorized' || !masterLayer(state)) return null;
  return { scenarios: createScenario(state.scenarios, name, state.classificationConfig, id, now) };
}

export const showScenario = (state: AppState, id: string | null): Pick<AppState, 'scenarios'> =>
  ({ scenarios: { ...state.scenarios, active: id && state.scenarios.items.some((item) => item.id === id) ? id : null } });

export function renameScenario(state: AppState, id: string, name: string): Pick<AppState, 'scenarios'> | null {
  const scenario = state.scenarios.items.find((item) => item.id === id); const clean = name.trim().slice(0, 60);
  return scenario && clean ? { scenarios: replaceScenario(state.scenarios, { ...scenario, name: clean }) } : null;
}

export function deleteScenario(state: AppState, id: string): Pick<AppState, 'scenarios'> {
  const items = state.scenarios.items.filter((item) => item.id !== id);
  return { scenarios: { active: state.scenarios.active === id ? null : state.scenarios.active, items } };
}

/** A category for a proposed value: the existing one, or a new `proposed` style with an unused palette colour. */
function withCategory(config: ClassificationConfig, value: string, label: string): ClassificationConfig {
  if (config.categories.some((category) => category.value === value)) return config;
  const used = new Set(config.categories.map(({ color }) => color));
  const color = config.palette.find((item) => !used.has(item)) ?? colorForCategory(value);
  const category: CategoryStyle = { value, label: label.trim() || value, color, visible: true, proposed: true };
  return { ...config, categories: [...config.categories, category] };
}

export interface ScenarioPreviewRow {
  readonly target: ScenarioTarget;
  readonly label: string;
  /** Active ha inside what is classified now (DisplaySet ∩ ThemeTarget): the area the map and the indicators change. */
  readonly areaHa: number;
  /** Active ha of the whole entity (the assignment covers all of it, also parts hidden by filters or the theme target). */
  readonly entityHa: number;
  /** Categories it shows in «Actual» (largest first; a Suerte split by Áreas parciales shows them all). */
  readonly actual: string;
  /** Categories it shows in the scenario before this assignment. */
  readonly current: string;
}

export interface ScenarioPreview {
  readonly rows: readonly ScenarioPreviewRow[];
  readonly skipped: number;
  /** Sum of `areaHa`: what the indicators will move. */
  readonly totalHa: number;
  /** Sum of `entityHa`: the selection as a whole. */
  readonly entityHa: number;
  /** Rows with no surface inside what is classified (assigned, but invisible on the map and in the indicators). */
  readonly outside: number;
}

const entityLabel = (state: AppState, target: ScenarioTarget): string => {
  const model = state.model!;
  if (target.level === 'suerte') {
    const suerte = model.suertes.get(target.id)!; const finca = model.fincas.get(suerte.fincaId);
    return `${finca?.name ? `${finca.name} · ` : ''}Suerte ${suerte.label}`;
  }
  const tablon = model.tablones.get(target.id)!; const suerte = model.suertes.get(tablon.suerteId); const finca = suerte ? model.fincas.get(suerte.fincaId) : undefined;
  return `${finca?.name ? `${finca.name} · ` : ''}Suerte ${suerte?.label ?? '?'} · Tablón ${tablon.code}`;
};

const targetFragments = (state: AppState, target: ScenarioTarget): readonly string[] =>
  (target.level === 'suerte' ? state.model!.suertes.get(target.id)?.fragmentIds : state.model!.tablones.get(target.id)?.fragmentIds) ?? [];

/** Categories of a target, largest first: by its parts when it is an entity of «Ver por», else by fragment. */
function targetCategories(state: AppState, theme: MasterTheme, target: ScenarioTarget, fragments: readonly string[]): { readonly actual: string; readonly current: string } {
  const assignments = theme.assignments as ScenarioAssignments;
  const actual = new Map<string, number>(); const current = new Map<string, number>();
  const add = (map: Map<string, number>, value: string, area: number) => map.set(value, (map.get(value) ?? 0) + area);
  const flows = target.level === state.entityViewLevel ? assignments.flows.filter(({ entityId }) => entityId === target.id) : [];
  // A Tablón of a Suerte split by Áreas parciales: measured against those pieces, as the map composes it.
  const split = target.level === 'tablon' ? theme.partialPieces?.get(state.model!.tablones.get(target.id)?.suerteId ?? '') : undefined;
  if (flows.length) flows.forEach(({ from, to, areaHa }) => { add(actual, from, areaHa); add(current, to, areaHa); });
  else if (split) { const parts = partialFragmentCategories(state.model!, split, theme.scenario ?? null, fragments); parts.actual.forEach((area, value) => add(actual, value, area)); parts.proposed.forEach((area, value) => add(current, value, area)); }
  else fragments.forEach((id) => { const area = activeArea(state.model!, [id]); add(actual, assignments.actualByFragmentId.get(id) ?? '—', area); add(current, assignments.categoryByFragmentId.get(id) ?? '—', area); });
  const join = (map: ReadonlyMap<string, number>) => [...map].sort((a, b) => b[1] - a[1]).map(([value]) => value).join(' + ') || '—';
  return { actual: join(actual), current: join(current) };
}

/**
 * What «Asignar en escenario» would change for the current selection, with the same semantics as the indicators: the
 * classified area (DisplaySet ∩ ThemeTarget, from the theme the map draws) and, apart, the whole entity area.
 */
export function previewScenarioAssignment(state: AppState): ScenarioPreview | null {
  const layer = masterLayer(state);
  if (!state.model || !layer || scenarioEditBlock(state)) return null;
  const theme = masterThemeAssignments(state, layer); if (!theme?.scenario) return null;
  const assignments = theme.assignments as ScenarioAssignments;
  const { targets, skipped } = scenarioTargets(state.model, state.selectionSet);
  const rows = targets.map((target): ScenarioPreviewRow => {
    const fragments = targetFragments(state, target); const inside = fragments.filter((id) => assignments.categoryByFragmentId.has(id));
    return { target, label: entityLabel(state, target), areaHa: activeArea(state.model!, inside), entityHa: activeArea(state.model!, fragments), ...targetCategories(state, theme, target, inside) };
  });
  return { rows, skipped, totalHa: rows.reduce((sum, row) => sum + row.areaHa, 0), entityHa: rows.reduce((sum, row) => sum + row.entityHa, 0), outside: rows.filter(({ areaHa }) => areaHa <= 0).length };
}

type ScenarioPatch = Partial<Pick<AppState, 'scenarios' | 'classificationConfig' | 'thematicSchemes' | 'legendConfig'>>;

/** «Asignar en escenario»: the selected Suertes and Tablones take `value` in the shown scenario. */
export function assignSelectionToScenario(state: AppState, value: string, label = value, now = new Date().toISOString()): ScenarioPatch | null {
  const scenario = shownScenario(state.scenarios); const category = value.trim();
  if (!scenario || !state.model || !category || scenarioEditBlock(state)) return null;
  const { targets } = scenarioTargets(state.model, state.selectionSet); if (!targets.length) return null;
  const scenarios = replaceScenario(state.scenarios, assignScenario(scenario, state.model, targets, category, now));
  const config = withCategory(state.classificationConfig, category, label);
  return config === state.classificationConfig ? { scenarios } : { scenarios, ...commitClassification(state, config) };
}

/** «Quitar de la propuesta»: the selected entities show their «Actual» category again in this scenario. */
export function clearSelectionFromScenario(state: AppState, now = new Date().toISOString()): Pick<AppState, 'scenarios'> | null {
  const scenario = shownScenario(state.scenarios); if (!scenario || !state.model || scenarioEditBlock(state)) return null;
  const { targets } = scenarioTargets(state.model, state.selectionSet); if (!targets.length) return null;
  return { scenarios: replaceScenario(state.scenarios, clearScenarioTargets(scenario, state.model, targets, now)) };
}

/** Remove one assignment (from the list of the panel). */
export function clearScenarioTarget(state: AppState, target: ScenarioTarget, now = new Date().toISOString()): Pick<AppState, 'scenarios'> | null {
  const scenario = shownScenario(state.scenarios); if (!scenario || !state.model || scenarioEditBlock(state)) return null;
  return { scenarios: replaceScenario(state.scenarios, clearScenarioTargets(scenario, state.model, [target], now)) };
}

/** «Revertir todo»: the scenario keeps its name and field and loses every assignment. */
export function revertScenario(state: AppState, now = new Date().toISOString()): Pick<AppState, 'scenarios'> | null {
  const scenario = shownScenario(state.scenarios); if (!scenario || scenarioEditBlock(state)) return null;
  return { scenarios: replaceScenario(state.scenarios, { ...scenario, overrides: { suerte: {}, tablon: {} }, updatedAt: now }) };
}

export interface ScenarioAssignmentRow { readonly target: ScenarioTarget; readonly label: string; readonly value: string; readonly areaHa: number }

/** Assignments of the shown scenario, as a list (Suertes then Tablones, by label). */
export function scenarioAssignmentRows(state: AppState, scenario: ClassificationScenario): readonly ScenarioAssignmentRow[] {
  if (!state.model) return [];
  const rows = (level: 'suerte' | 'tablon', record: Readonly<Record<string, string>>) => Object.entries(record).map(([id, value]) => {
    const target = { level, id } as const;
    const exists = level === 'suerte' ? state.model!.suertes.has(id) : state.model!.tablones.has(id);
    return { target, label: exists ? entityLabel(state, target) : `${level === 'suerte' ? 'Suerte' : 'Tablón'} sin superficie activa (${id.split('/').at(-1)})`, value, areaHa: exists ? activeArea(state.model!, targetFragments(state, target)) : 0 };
  });
  return [...rows('suerte', scenario.overrides.suerte), ...rows('tablon', scenario.overrides.tablon)].sort((a, b) => a.label.localeCompare(b.label, 'es', { numeric: true }));
}

/** One step of «Deshacer» in the Escenarios panel: the scenarios before and after, and the categories the step created. */
export interface ScenarioUndoStep {
  readonly before: ScenarioState;
  readonly after: ScenarioState;
  readonly addedCategories: readonly string[];
}

/** Records a scenario patch just applied (`state` is the state before it, `after` the scenarios it produced). */
export function scenarioUndoStep(state: AppState, patch: ScenarioPatch, after: ScenarioState): ScenarioUndoStep {
  const before = new Set(state.classificationConfig.categories.map(({ value }) => value));
  const addedCategories = (patch.classificationConfig?.categories ?? []).filter(({ value, proposed }) => proposed && !before.has(value)).map(({ value }) => value);
  return { before: state.scenarios, after, addedCategories };
}

/** Whether a recorded step still applies: the scenarios themselves (not just which one is shown or the mark) are as it left them. */
export const scenarioStepApplies = (state: AppState, step: ScenarioUndoStep): boolean => state.scenarios.items === step.after.items;

/**
 * Reverts a step completely: the scenarios AND the proposed categories it created (style and alias), so nothing of it
 * stays in the classification. The scenario shown and the mark stay as they are now. Null when the scenarios changed in
 * some other way since (another project was opened, for instance): that history no longer applies.
 */
export function undoScenarioStep(state: AppState, step: ScenarioUndoStep): ScenarioPatch | null {
  if (!scenarioStepApplies(state, step)) return null;
  const items = step.before.items; const current = state.scenarios.active;
  const scenarios: ScenarioState = { ...state.scenarios, items, active: current && items.some(({ id }) => id === current) ? current : step.before.active };
  const config = state.classificationConfig;
  const categories = config.categories.filter(({ value, proposed }) => !(proposed && step.addedCategories.includes(value)));
  return categories.length === config.categories.length ? { scenarios } : { scenarios, ...commitClassification(state, { ...config, categories }) };
}
